import { obterTodosCompradoresPFEPJ } from '@/features/proposta/brasil/domain/Comprador/merge-comprador-pj-pf'
import type { TPropostaSchemaInput } from '@/features/proposta/brasil/domain/schemas'
import { Cnpj } from '@/utils/cnpj'
import { Cpf } from '@/utils/cpf'

type CompradorBase = {
  id: string
  nome: string
  documento: string
  tipo: 'PessoaFisica' | 'PessoaJuridica'
}

export type CompradorResumo = {
  nome: string
  documento: string
  participacao: string
  tipoDocumento: 'CPF' | 'CNPJ'
}

export class ResumoCompradores {
  readonly compradores: CompradorResumo[] = []
  readonly titular: CompradorResumo

  constructor(
    cadastro: TPropostaSchemaInput['cadastroCompradores'],
    participacao: TPropostaSchemaInput['detalhesDaProposta']['participacaoCompradores'],
  ) {
    const compradoresETitular = this.buildListaDeCompradores(cadastro)
    this.titular = this.definirTitular(compradoresETitular, participacao)
    this.compradores = this.definirDemaisCompradores(compradoresETitular, participacao)
  }

  private buildListaDeCompradores(cadastro: TPropostaSchemaInput['cadastroCompradores']): CompradorBase[] {
    const compradores = obterTodosCompradoresPFEPJ(cadastro.compradores, cadastro.clientes, cadastro.clientesJuridicos)

    return compradores.map((c) => ({
      id: c.id,
      nome: c.nome,
      documento: c.documento,
      tipo: c.tipo,
    }))
  }

  private definirTitular(
    todos: CompradorBase[],
    participacao: TPropostaSchemaInput['detalhesDaProposta']['participacaoCompradores'],
  ): CompradorResumo {
    if (todos.length === 1) {
      const primeiro = todos[0]!
      return {
        nome: primeiro.nome,
        documento: this.formatDocumento(primeiro),
        participacao: '100%',
        tipoDocumento: primeiro.tipo == 'PessoaFisica' ? 'CPF' : 'CNPJ',
      }
    }

    const titularBase = todos.find((c) => c.id === participacao.titular.id)!
    return {
      nome: titularBase.nome,
      documento: this.formatDocumento(titularBase),
      participacao: `${participacao.titular.percentualParticipacao}%`,
      tipoDocumento: titularBase.tipo == 'PessoaFisica' ? 'CPF' : 'CNPJ',
    }
  }

  private definirDemaisCompradores(
    todos: CompradorBase[],
    participacao: TPropostaSchemaInput['detalhesDaProposta']['participacaoCompradores'],
  ): CompradorResumo[] {
    if (!participacao.titular.id) return []

    return todos
      .filter((c) => c.id !== participacao.titular.id)
      .map((c) => ({
        nome: c.nome,
        documento: this.formatDocumento(c),
        participacao: `${participacao.compradores.find((p) => p.id === c.id)!.percentualParticipacao}%`,
        tipoDocumento: c.tipo == 'PessoaFisica' ? 'CPF' : 'CNPJ',
      }))
  }

  private formatDocumento(c: CompradorBase): string {
    return c.tipo === 'PessoaFisica' ? new Cpf(c.documento).format() : new Cnpj(c.documento).format()
  }
}
